import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref, watch, type Ref } from 'vue';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput';
import CImageUploadRow from '@/components/shared/CImageUploadRow';
import type { CSelectItem } from '@/components/shared/CSelect';
import FontSelector from '../../../FontSelector.vue';
import ColorSelector from '../../../ColorSelector.vue';
import { useClickOutside } from '@/composables/clickOutside';
import { useToaster } from '@/composables/toaster';
import { useFonts } from '@/composables/fonts';
import { getCrosswordTileUploadSignedUrl } from '@/api/crossword';
import { uploadFileToS3 } from '@/utils/s3Utils';
import { detectFileType, isValidFileSignature } from '@/components/shared/CFileInput/utils/fileSignatureValidation';
import { buildCloudFrontUrl } from '@/utils/pathUtils';
import type { ColorObject } from '@/typings/libraries';
import type { FontColorOption } from '@/typings/customilyElements/canvasElements';
import { DEFAULT_STYLE_LETTERS } from '../../core/presets';
import type { CrosswordStyle, CrosswordStyleDraft } from '../../types';


export default /*#__PURE__*/_defineComponent({
  __name: 'CrosswordCustomStyleModal',
  props: {
    value: { type: Boolean },
    defaultName: null,
    styleToEdit: null
  },
  emits: ["input", "save", "back"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'input', value: boolean): void;
    (event: 'save', value: CrosswordStyleDraft): void;
    (event: 'back'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    value: boolean;
    defaultName?: string;
    /** Style being edited; when absent the modal creates a new one */
    styleToEdit?: CrosswordStyle | null;
};

const MIN_FONT_SIZE = 4;
const MAX_FONT_SIZE = 400;
const TILE_IMAGE_ACCEPT = '.png,.jpg,.jpeg,.webp';
const TILE_IMAGE_MAX_MB = 10;





const toaster = useToaster();
const { userFonts, generateFontName, getFontFromPath } = useFonts();

const tileFiles = ref<File[]>([]);

const model = reactive({
    name: '',
    font: undefined as CSelectItem<string> | undefined,
    fontSize: DEFAULT_STYLE_LETTERS.fontSize,
    color: { hex: DEFAULT_STYLE_LETTERS.fontColor, alpha: 1 } as ColorObject,
    tileImage: null as string | null
});

const tileImageName = ref('');

const uploading = ref(false);
const pickerRef: Ref<HTMLElement | undefined> = ref();

const { clickOutsideActive } = useClickOutside(
    pickerRef,
    () => {
        clickOutsideActive.value = false;
    },
    false
);

const fontSizeIsValid = computed(() => model.fontSize >= MIN_FONT_SIZE && model.fontSize <= MAX_FONT_SIZE);

// A custom style is only usable with its tile, name, font and a size the generator accepts
const canCreate = computed(
    () => !!model.name.trim() && !!model.tileImage && !!model.font?.value && fontSizeIsValid.value && !uploading.value
);
const tileImageUrl = computed(() => (model.tileImage ? buildCloudFrontUrl(model.tileImage) : ''));

const toggleColorPicker = () => {
    clickOutsideActive.value = !clickOutsideActive.value;
};

const onColorSelected = (color: FontColorOption) => {
    model.color = color as ColorObject;
    toggleColorPicker();
};

const onTileImageUpload = async (files: File[]) => {
    const [file] = files;
    if (!file) return;

    uploading.value = true;
    try {
        if (!(await isValidFileSignature(file, TILE_IMAGE_ACCEPT))) {
            throw new Error(`This file is not a valid image (detected: ${(await detectFileType(file)) ?? 'unknown'})`);
        }

        const signedUrl = await getCrosswordTileUploadSignedUrl(file.name);
        await uploadFileToS3(signedUrl.SignedUrl, signedUrl.Mime, file, signedUrl.CacheControl);
        model.tileImage = signedUrl.Path;
        // The stored file is a guid, so the row keeps showing the name the user picked
        tileImageName.value = file.name;
    } catch (error) {
        console.error(error);
        toaster.error('We could not upload the tile image, please try again.');
    } finally {
        // The uploaded path is the source of truth, keeping the file would flip CFileInput to preview mode
        tileFiles.value = [];
        uploading.value = false;
    }
};

const removeTileImage = () => {
    model.tileImage = null;
    tileImageName.value = '';
    tileFiles.value = [];
};

const getFileName = (path: string) => path.split('/').pop() || '';

const fontItem = (path: string): CSelectItem<string> | undefined => {
    const font = getFontFromPath(path);
    return font ? { label: generateFontName(font), value: font.Path } : undefined;
};

const defaultFontItem = (): CSelectItem<string> | undefined =>
    fontItem(DEFAULT_STYLE_LETTERS.font) ||
    (() => {
        const arial = userFonts.value.find((userFont) => userFont.Name.toLowerCase() === 'arial');
        return arial ? { label: generateFontName(arial), value: arial.Path } : undefined;
    })();

const isEditing = computed(() => !!props.styleToEdit);

const reset = () => {
    const style = props.styleToEdit;

    model.name = style?.name ?? props.defaultName ?? '';
    model.font = style?.font ? fontItem(style.font) : defaultFontItem();
    model.fontSize = style?.fontSize ?? DEFAULT_STYLE_LETTERS.fontSize;
    model.color = { hex: style?.fontColor ?? DEFAULT_STYLE_LETTERS.fontColor, alpha: 1 } as ColorObject;
    model.tileImage = style?.tileImage ?? null;
    tileImageName.value = style?.tileImage ? getFileName(style.tileImage) : '';
    tileFiles.value = [];
    clickOutsideActive.value = false;
};

const handleSave = () => {
    if (!canCreate.value) return;

    emit('save', {
        name: model.name.trim(),
        custom: true,
        tileImage: model.tileImage,
        // A custom style is previewed by its own tile, there is no catalog thumbnail for it
        thumbnail: model.tileImage || undefined,
        font: model.font!.value,
        fontSize: model.fontSize,
        fontColor: model.color.hex
    });
};

const handleBack = () => {
    emit('back');
};

watch(
    () => props.value,
    (isOpen) => {
        if (isOpen) reset();
    },
    { immediate: true }
);

// The fonts may still be loading when the modal opens
watch(userFonts, () => {
    if (model.font) return;
    model.font = props.styleToEdit?.font ? fontItem(props.styleToEdit.font) : defaultFontItem();
});

return { __sfc: true,MIN_FONT_SIZE, MAX_FONT_SIZE, TILE_IMAGE_ACCEPT, TILE_IMAGE_MAX_MB, props, emit, toaster, userFonts, generateFontName, getFontFromPath, tileFiles, model, tileImageName, uploading, pickerRef, clickOutsideActive, fontSizeIsValid, canCreate, tileImageUrl, toggleColorPicker, onColorSelected, onTileImageUpload, removeTileImage, getFileName, fontItem, defaultFontItem, isEditing, reset, handleSave, handleBack, CModal, CButton, CInput, CImageUploadRow, FontSelector, ColorSelector }
}

})